import { useCurrentDate, Button, Card } from "@sellar-io/components";
import { useState } from "react";
import { useStockListOrderSummaryProps } from "../../../../../../stockList/useStockListOrderSummaryProps";
import { ContextOrderBasketType } from "../../../../../../../hooks";
import { DraftOrdersListResponseType } from "@sellar-io/api-v2/types";
import { useMediaQuery } from "usehooks-ts";
import { getNextDirectDeliveryDate } from "../../../../../helpers/DeliveryDateHelpers/getNextDeliveryDate";
import { twMerge } from "tailwind-merge";
import { Icon, ProfilePicture } from "../../../../../../../components";
import { FulfillmentTypeBadgeDraftBasket } from "./FulfillmentTypeBadgeDraftBasket";
import dayjs from "dayjs";
import {
  findLatestPreOrderAvailableFromDate,
  pluralize,
} from "@sellar-io/common";
import { OrderItemCardList } from "./OrderItemCardList";
import { OrderItemDetailsList } from "./OrderItemDetailsList";
import { formattedOrderItemList } from "../../../helpers/formattedOrderItemList";
import { OrderRulesSummaryDraftBasket } from "./OrderRulesSummaryDraftBasket";
import { usePaymentTermsIndicator } from "../../../../../../../components/paymentTypeIndicator/usePaymentTermsIndicator";
import { useBuyerRelationshipList } from "../../../../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useRemoteFeatureFlags } from "../../../../../../../hooks/useRemoteFeatureFlags";

export const DraftOrderItemCard = ({
  order,
  supplierStorefront,
  supplier,
  className,
  onEditBasket,
  onDeleteBasket,
  onCheckoutBasket,
}: {
  order: ContextOrderBasketType;
  supplierStorefront: DraftOrdersListResponseType[number]["storefront"];
  supplier: DraftOrdersListResponseType[number]["supplier"];
  className?: string;
  onEditBasket: () => void;
  onDeleteBasket: () => void;
  onCheckoutBasket: () => void;
}) => {
  const currentDate = useCurrentDate();
  const [detailsShown, setDetailsShown] = useState(false);
  const fulfillmentType = supplierStorefront?.FulfillmentType.fulfillmentType;
  const buyerRelationshipsQuery = useBuyerRelationshipList({});

  const buyerRelationship = (
    buyerRelationshipsQuery.data?.pages.flatMap((page) => page) ?? []
  ).find((rr) => rr.Relationship.SupplierId === supplier.id);

  const buyerRelationshipDetails =
    buyerRelationship?.Relationship.BuyerRelationshipDetails;
  const numberOfItems =
    order.OrderItems?.reduce(
      (acc, currentItem) => acc + currentItem.quantity,
      0,
    ) ?? 0;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    apiValues: { price: orderTotalApi, subTotal: subTotalApi },
    haveMoqRulesBeenSatisfied,
    hasMinimumOrderPriceBeenMet,
    partiallySatisfiedCaseSizeRules,
    delivery,
    subTotal,
    discount,
    tax,
    total,
  } = useStockListOrderSummaryProps({
    order,
    deliveryChargeRules:
      supplierStorefront?.FulfillmentType.DeliveryChargeRules,
    deliveryCharge: supplierStorefront?.FulfillmentType.charge,
    fulfillmentTypeContainerDeliveryCharges:
      supplierStorefront?.FulfillmentType
        .FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    containerTypeDeliveryChargeRules:
      supplierStorefront?.FulfillmentType.ContainerTypeDeliveryChargeRules ??
      [],
    freeDeliveryBasketSubtotal,
  });

  const hasSatisfiedCaseSizeRules = supplierStorefront?.CaseSizeRules.enforced
    ? !partiallySatisfiedCaseSizeRules.length
    : true;

  const hasSatisfiedOrderRules =
    haveMoqRulesBeenSatisfied &&
    hasMinimumOrderPriceBeenMet &&
    hasSatisfiedCaseSizeRules;

  const subtotalFreeDeliveryThreshold =
    supplierStorefront?.FulfillmentType.DeliveryChargeRules[0]?.value ?? 0;

  const isMobile = useMediaQuery("(max-width: 700px)");

  const latestAvailablePreOrderFromDate = findLatestPreOrderAvailableFromDate(
    order.OrderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [],
  );

  const estimatedNextDeliveryDates = supplierStorefront?.DeliveryArea
    ? getNextDirectDeliveryDate({
        deliveryArea: supplierStorefront.DeliveryArea,
        startDateReference: latestAvailablePreOrderFromDate ?? currentDate,
      })
    : null;

  const isImplicitProformaActive =
    useRemoteFeatureFlags().isActive("implicitProforma");

  const { full, type } = usePaymentTermsIndicator({
    connection: buyerRelationshipDetails
      ? {
          ...buyerRelationshipDetails,
          paymentDue: buyerRelationshipDetails.paymentDue ?? null,
          paymentDueCalculateFrom:
            buyerRelationshipDetails.paymentDueCalculateFrom ?? null,
          paymentTerms: buyerRelationshipDetails.paymentTerms ?? null,
        }
      : null,
    invoiceCreationStage: supplier.invoiceCreationStage,
    approved: buyerRelationshipDetails?.approved ?? false,
    paymentTermsRequest: buyerRelationshipDetails?.PaymentTermsRequest,
    hasDefaultPriceTier: true,
    supplier,
  });

  // When implicitProforma is active, only show credit and sellarCredit tags
  const shouldShowPaymentTermsIndicator =
    !isImplicitProformaActive || type === "credit" || type === "sellarCredit";

  return (
    <Card className={twMerge("bg-white", "divide-y divide-solid", className)}>
      <div className="flex flex-col gap-2 px-4 py-4">
        {/* supplier profile image and payment terms */}
        {isMobile ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <ProfilePicture
                size={"medium"}
                logoUrl={supplier.Business.logoUrl}
                fallback="supplier-badge"
              />
              <div className="text-lg font-semibold text-zinc-800">
                {supplier.Business.name}
              </div>
            </div>

            <div className={"flex gap-4"}>
              {shouldShowPaymentTermsIndicator && full}

              <FulfillmentTypeBadgeDraftBasket
                fulfillmentType={fulfillmentType ?? null}
                estimatedDeliveryDate={dayjs(
                  estimatedNextDeliveryDates?.deliveryDate,
                )}
                isSupplier={false}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center gap-2">
              <ProfilePicture
                size={"medium"}
                logoUrl={supplier.Business.logoUrl}
                fallback="supplier-badge"
              />
              <div className="text-lg font-semibold text-zinc-800">
                {supplier.Business.name}
              </div>
            </div>

            <div className={"flex gap-2"}>
              {shouldShowPaymentTermsIndicator && full}

              <FulfillmentTypeBadgeDraftBasket
                fulfillmentType={fulfillmentType ?? null}
                estimatedDeliveryDate={dayjs(
                  estimatedNextDeliveryDates?.deliveryDate,
                )}
                isSupplier={false}
              />
            </div>
          </div>
        )}

        {/* order item cards and price total */}

        {isMobile ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              {/* total price */}
              <div className="text-zinc-800">
                <span className="font-semibold">
                  {numberOfItems} {pluralize("item", numberOfItems)} total:{" "}
                </span>
                <span>{total}</span>
              </div>

              {/* show details */}
              <div>
                <Button
                  className={
                    "justify-end text-base font-normal text-zinc-500 normal-case"
                  }
                  size={"small"}
                  color={"transparent"}
                  onClick={() =>
                    setDetailsShown((currentShowDetails) => !currentShowDetails)
                  }
                >
                  {detailsShown ? "Less details" : "Show details"}
                  <Icon icon={detailsShown ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
                </Button>
              </div>
            </div>

            {!detailsShown && (
              <OrderItemCardList orderItems={order.OrderItems ?? []} />
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                {/* total price */}
                <div className="mb-2 text-zinc-800">
                  <span className="font-semibold">
                    {numberOfItems} {pluralize("item", numberOfItems)}{" "}
                    total:{" "}
                  </span>
                  <span>{total}</span>
                </div>

                {/* items in basket cards */}
                {!detailsShown && (
                  <OrderItemCardList orderItems={order.OrderItems ?? []} />
                )}
              </div>

              {/* show details */}
              <div>
                <Button
                  className={
                    "justify-end text-base font-normal text-zinc-500 normal-case"
                  }
                  size={"small"}
                  color={"transparent"}
                  onClick={() =>
                    setDetailsShown((currentShowDetails) => !currentShowDetails)
                  }
                >
                  {detailsShown ? "Less details" : "Show details"}
                  <Icon icon={detailsShown ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
                </Button>
              </div>
            </div>
          </>
        )}
        {detailsShown && (
          <OrderItemDetailsList
            orderItemList={order.OrderItems}
            delivery={delivery ?? ""}
            subTotal={subTotal ?? ""}
            discount={discount}
            tax={tax}
            total={total}
            fulfillmentType={fulfillmentType ?? null}
            deliveryArea={supplierStorefront?.DeliveryArea}
          />
        )}

        {/* shows outstanding order rules and free delivery progress */}
        <OrderRulesSummaryDraftBasket
          orderTotal={orderTotalApi}
          supplierUrlId={supplier.urlId}
          priceTierOrderRules={priceTierOrderRules}
          minimumOrderPrice={minimumOrderPrice}
          supplierStorefront={supplierStorefront ?? undefined}
          currency={supplier.currency}
          orderItemList={formattedOrderItemList(order.OrderItems)}
          partiallySatisfiedCaseSizeRuleList={partiallySatisfiedCaseSizeRules}
          subTotalApi={subTotalApi}
          subtotalFreeDeliveryThreshold={subtotalFreeDeliveryThreshold}
        />
      </div>

      {/* basket checkout and edit options */}
      {isMobile ? (
        <div className="flex flex-col items-center justify-between gap-2 px-4 py-4">
          {hasSatisfiedOrderRules && (
            <div className="w-full grow">
              <Button
                className="w-full"
                color="primary"
                onClick={onCheckoutBasket}
              >
                GO TO CHECKOUT
              </Button>
            </div>
          )}

          <div className="flex w-full gap-2">
            <div>
              <Button
                className={"justify-end"}
                color={"transparent"}
                style={{
                  color: "red",
                  border: "solid #D4D4D8 1px",
                }}
                onClick={onDeleteBasket}
              >
                <Icon icon={"DELETE"} />
              </Button>
            </div>
            <div className="grow">
              <Button
                color="secondary"
                style={{ width: "100%" }}
                onClick={onEditBasket}
              >
                EDIT BASKET
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between px-4 py-4">
          <div>
            <Button
              className={"justify-end"}
              color={"transparent"}
              style={{ color: "red" }}
              onClick={onDeleteBasket}
            >
              <Icon icon={"DELETE"} />
              REMOVE BASKET
            </Button>
          </div>

          <div className="flex gap-2">
            <div>
              <Button color="secondary" onClick={onEditBasket}>
                EDIT BASKET
              </Button>
            </div>

            {hasSatisfiedOrderRules && (
              <div>
                <Button color="primary" onClick={onCheckoutBasket}>
                  GO TO CHECKOUT
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};
